import { Button } from "@/components/ui-kit/button";
import { Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';

type PresetQuestionsListProps = {
  questions: string[];
  onChange: (questions: string[]) => void;
  onSave: (questions: string[]) => Promise<void>;
};
export const PresetQuestionsList = ({ questions, onChange, onSave }: PresetQuestionsListProps) => {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const focusedValuesRef = useRef<Map<number, string>>(new Map());

  useEffect(() => {
    if (questions.length === 0) {
      onChange([""]);
    }
  }, [questions, onChange]);

  const handleQuestionChange = (index: number, value: string) => {
    const newQuestions = [...questions];
    newQuestions[index] = value;
    onChange(newQuestions);
  };

  const handleDelete = async (index: number) => {
    if (questions.length <= 1) {
      return;
    }

    setIsSaving(true);
    const newQuestions = questions.filter((_, i) => i !== index);
    const payload = newQuestions.map((q) => q.trim() || "  ").filter(Boolean);
    onChange(newQuestions);
    await onSave(payload);
    setIsSaving(false);
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();

      if (questions.length < 5 && !isSaving) {
        onChange([...questions, ""]);
      }
    }
  };

  const handleFocus = (index: number) => {
    focusedValuesRef.current.set(index, questions[index]);
  };

  const handleBlur = async (index: number) => {
    const originalValue = focusedValuesRef.current.get(index);

    if (originalValue !== undefined && questions[index] !== originalValue) {
      setIsSaving(true);
      const payload = questions.map((q) => q.trim() || "  ").filter(Boolean);
      await onSave(payload);
      setIsSaving(false);
    }

    focusedValuesRef.current.delete(index);
  };

  const handleAddQuestion = () => {
    if (questions.length < 5 && !isSaving) {
      onChange([...questions, ""]);
    }
  };

  return (
    <div className="space-y-3">
      {questions.map((question, index) => (
        <div key={index} className="flex items-center gap-2">
          <input
            type="text"
            value={question}
            onChange={(e) => handleQuestionChange(index, e.target.value)}
            onFocus={() => handleFocus(index)}
            onKeyDown={(e) => handleKeyDown(e)}
            onBlur={() => handleBlur(index)}
            className="flex-1 rounded-lg border bg-card px-4 py-2.5 text-sm focus:border-primary focus:outline-none"
            placeholder={t('QUESTION_PLACEHOLDER')}
            disabled={isSaving}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => handleDelete(index)}
            className="text-medium-emphasis hover:text-error"
            disabled={isSaving || questions.length <= 1}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}

      {questions.length < 5 && (
        <Button
          type="button"
          variant="outline"
          className="w-fit"
          onClick={handleAddQuestion}
          disabled={isSaving}
        >
          {t('ADD_QUESTION_COUNT', { current: questions.length, max: 5 })}
        </Button>
      )}
    </div>
  );
};
